import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { catchError, distinctUntilChanged, finalize, map, of, switchMap } from 'rxjs';
import { WnPerms } from '../../../../core/auth/worknest-admin-permissions';
import { apiErrorFallback, extractApiErrorMessage } from '../../../../core/http/extract-api-error-message';
import { Translation } from '../../../../core/i18n/translation.service';
import { AssetCharge, AssetChargeReason, AssetChargeStatus } from '../../../../core/models/assets/asset-charges.dto';
import { AssetChargesService } from '../../../../core/services/asset-charges.service';
import { PermissionService } from '../../../../core/services/permission.service';
import { ToastFeedbackService } from '../../../../core/services/toast-feedback.service';
import { AdminBackLinkComponent } from '../../../../shared/components/admin-back-link/admin-back-link.component';
import { StatusChip } from '../../../../shared/components/status-chip/status-chip';
import { chargeAssetLabel } from './asset-charge-labels';
import { LinkAssetDialogComponent } from './link-asset-dialog/link-asset-dialog.component';
import { WaiveChargeDialogComponent } from './waive-charge-dialog/waive-charge-dialog.component';

/** One charge: its facts, the deduction schedule, and the waive / link-asset actions. */
@Component({
  selector: 'app-asset-charge-detail-page',
  standalone: true,
  imports: [DatePipe, RouterLink, TranslatePipe, ButtonModule, AdminBackLinkComponent, StatusChip, WaiveChargeDialogComponent, LinkAssetDialogComponent],
  templateUrl: './asset-charge-detail-page.component.html',
  styleUrl: './asset-charge-detail-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssetChargeDetailPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly charges = inject(AssetChargesService);
  private readonly permissions = inject(PermissionService);
  private readonly translation = inject(Translation);
  private readonly toast = inject(ToastFeedbackService);

  protected readonly charge = signal<AssetCharge | null>(null);
  protected readonly loading = signal(true);
  protected readonly loadError = signal<string | null>(null);
  protected readonly waiveOpen = signal(false);
  protected readonly linkOpen = signal(false);
  private readonly canManage = computed(() => this.permissions.hasAny(WnPerms.assets.chargeManage));
  private readonly isArabic = computed(() => this.translation.getCurrentLang().toLowerCase().startsWith('ar'));

  /** Waive needs manage rights and an Active charge with something left (the server refuses anything else). */
  protected readonly canWaive = computed(() => {
    const c = this.charge();
    return this.canManage() && !!c && c.status === AssetChargeStatus.Active && c.remaining > 0;
  });
  /** Linking an asset is for purchase charges that have none yet. */
  protected readonly canLink = computed(() => {
    const c = this.charge();
    return this.canManage() && !!c && c.reason === AssetChargeReason.Purchase && !c.assetId;
  });
  protected readonly assetLabel = computed(() => {
    const c = this.charge();
    return c ? chargeAssetLabel(c, this.isArabic()) : '';
  });

  constructor() {
    this.route.paramMap
      .pipe(
        map((pm) => pm.get('id')),
        distinctUntilChanged(),
        switchMap((id) => {
          this.loading.set(true);
          this.loadError.set(null);
          if (!id) {
            this.loading.set(false);
            return of(null);
          }
          return this.charges.get(id).pipe(
            catchError((err) => {
              this.loadError.set(extractApiErrorMessage(err, apiErrorFallback(this.translation)));
              return of(null);
            }),
            finalize(() => this.loading.set(false)),
          );
        }),
        takeUntilDestroyed(),
      )
      .subscribe((charge) => this.charge.set(charge));
  }

  protected onWaived(): void {
    this.waiveOpen.set(false);
    this.toast.successKey('ASSET_CHARGES.WAIVE_DONE');
    this.refresh();
  }

  protected onLinked(): void {
    this.linkOpen.set(false);
    this.toast.successKey('ASSET_CHARGES.LINK_DONE');
    this.refresh();
  }

  private refresh(): void {
    const id = this.charge()?.id;
    if (!id) return;
    this.charges.get(id).subscribe((charge) => this.charge.set(charge));
  }
}
